import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../Dialog'
import { Field } from '../Field'
import { useEntitySave, makeEntity, now } from '../../api/hooks/useEntity'
import { ENTITY, type Grade, type GradeType, type Student, type Subject } from '../../types/entities'
import { localIsoDate } from '../../utils/dates'

/**
 * Массовое выставление оценок: один предмет / дата / тип, для каждого
 * ученика — своё значение. Пропускает учеников с пустым полем.
 */
export function BulkGradeDialog({
  classId,
  students,
  subjects,
  onClose,
}: {
  classId: string
  students: Student[]
  subjects: Subject[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const save = useEntitySave<Grade>(ENTITY.grade)
  const [subjectId, setSubjectId] = useState(subjects[0]?.id ?? '')
  const [date, setDate] = useState(() => localIsoDate())
  const [type, setType] = useState<GradeType>('test')
  const [topic, setTopic] = useState('')
  const [valueByStudent, setValueByStudent] = useState<Record<string, string>>({})

  const setVal = (sid: string, v: string) =>
    setValueByStudent((m) => ({ ...m, [sid]: v }))

  const handleSave = async () => {
    if (!subjectId) return
    const records: Grade[] = students
      .map((s) => ({ s, v: valueByStudent[s.id]?.trim() }))
      .filter((x): x is { s: Student; v: string } => Boolean(x.v))
      .map(({ s, v }) =>
        makeEntity<Grade>({
          classId,
          studentId: s.id,
          subjectId,
          date,
          value: v,
          scale: 'fivePoint',
          type,
          weight: 1.5, // контрольные имеют больший вес
          topic: topic.trim() || null,
          comment: null,
          createdAt: now(),
          updatedAt: now(),
        }),
      )
    if (records.length === 0) return
    await save.mutateAsync(records)
    onClose()
  }

  const filled = Object.values(valueByStudent).filter(Boolean).length

  return (
    <Dialog title={t('bulkGradeDialog.title')} onClose={onClose}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('bulkGradeDialog.date')}>
          <input type="date" className="input-field" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label={t('bulkGradeDialog.type')}>
          <select className="input-field" value={type} onChange={(e) => setType(e.target.value as GradeType)}>
            <option value="test">{t('bulkGradeDialog.typeTest')}</option>
            <option value="exam">{t('bulkGradeDialog.typeExam')}</option>
            <option value="project">{t('bulkGradeDialog.typeProject')}</option>
            <option value="homework">{t('bulkGradeDialog.typeHomework')}</option>
            <option value="current">{t('bulkGradeDialog.typeCurrent')}</option>
          </select>
        </Field>
      </div>
      <Field label={t('bulkGradeDialog.subject')}>
        <select className="input-field" value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <Field label={t('bulkGradeDialog.topic')}>
        <input
          className="input-field"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder={t('bulkGradeDialog.topicPlaceholder')}
        />
      </Field>

      <div className="text-xs text-gray-500 mb-2">
        {t('bulkGradeDialog.hint')}
        {' '}{t('bulkGradeDialog.fillProgress', { filled, total: students.length })}
      </div>

      <div className="max-h-[280px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {students.map((s) => (
          <div key={s.id} className="flex items-center gap-3 px-3 py-2">
            <div className="flex-1 truncate text-sm">
              {s.lastName} {s.firstName.charAt(0)}.
            </div>
            <input
              type="text"
              className="w-16 text-center text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-primary"
              placeholder="—"
              value={valueByStudent[s.id] ?? ''}
              onChange={(e) => setVal(s.id, e.target.value)}
              maxLength={3}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2 justify-end mt-4">
        <button onClick={onClose} className="btn-outline text-sm">{t('common.cancel')}</button>
        <button
          onClick={handleSave}
          disabled={save.isPending || filled === 0 || !subjectId}
          className="btn-primary text-sm"
        >
          {save.isPending ? t('bulkGradeDialog.saving') : t('bulkGradeDialog.submit', { filled })}
        </button>
      </div>
    </Dialog>
  )
}
